// Change password (docs/plans/sign-in.md, Screens): Current password, New password with the rules,
// Confirm new password. Markup from the Dialog and Input cards and the board.
import { useState, type FormEvent } from 'react';
import type { Api } from '../auth/api';
import { copy, rateLimitText } from '../auth/copy';
import { passwordRules } from '../auth/password-rules';
import { ErrorLine } from '../auth/SignInLayout';
import { Dialog } from './Dialog';
import { CheckIcon, DotIcon } from './icons';
import { useOnline } from './online';

type ChangePasswordDialogProps = {
  api: Api;
  email: string;
  onClose: () => void;
  onChanged: () => void;
};

export function ChangePasswordDialog({
  api,
  email,
  onClose,
  onChanged,
}: ChangePasswordDialogProps) {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rules = passwordRules(password, confirm, email);
  const online = useOnline();
  const canSave = current !== '' && rules.every((r) => r.ok) && !busy && online;

  async function save(e?: FormEvent) {
    e?.preventDefault();
    if (!canSave) return;
    setBusy(true);
    setError(null);
    const res = await api.changePassword(current, password, confirm);
    setBusy(false);
    if (res.ok) return onChanged();
    if (res.error === 'wrong_current') {
      setCurrent('');
      setError(copy.wrongCurrent);
    } else if (res.error === 'rate_limited') setError(rateLimitText(res));
    else setError(copy.somethingWrong);
  }

  return (
    <Dialog
      id="change-password-title"
      title={copy.changePassword}
      subline={email}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {copy.cancel}
          </button>
          <button
            type="submit"
            form="change-password-form"
            className="btn btn-primary ml-auto"
            disabled={!canSave}
          >
            {copy.changePassword}
          </button>
        </>
      }
    >
      <form id="change-password-form" className="col g16" noValidate onSubmit={save}>
        {/* Lets a password manager save the new password under the right email. */}
        <input type="email" autoComplete="username" value={email} readOnly hidden />
        <div>
          <label className="flab" htmlFor="cp-current">
            {copy.currentPassword} <span className="req">*</span>
          </label>
          <input
            id="cp-current"
            className="input"
            type="password"
            autoComplete="current-password"
            maxLength={128}
            aria-invalid={error === copy.wrongCurrent || undefined}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
        <div>
          <label className="flab" htmlFor="cp-new">
            {copy.newPassword} <span className="req">*</span>
          </label>
          <input
            id="cp-new"
            className="input"
            type="password"
            autoComplete="new-password"
            maxLength={128}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <ul className="pwrs" aria-label="Password rules">
            {rules.map((rule) => (
              <li key={rule.label} className={rule.ok ? 'pwr ok' : 'pwr'}>
                {rule.ok ? <CheckIcon className="ico s12" /> : <DotIcon className="ico s12" />}
                {rule.label}
                <span className="sr">{rule.ok ? ', done' : ', not yet'}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <label className="flab" htmlFor="cp-confirm">
            {copy.confirmNewPassword} <span className="req">*</span>
          </label>
          <input
            id="cp-confirm"
            className="input"
            type="password"
            autoComplete="new-password"
            maxLength={128}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        {error && <ErrorLine text={error} />}
      </form>
    </Dialog>
  );
}
